import { useLocalization } from 'localization';
import TextTooltip from 'components/v2/text/TextTooltip';
import { IUserBranchDto, IUserBranchWithStatusDto } from '../../../../api/users/dtos';
import { UserStatuses } from '../../../../constants/enums/status';
import { Button, Chip, useTheme } from '@mui/material';
import CircleIcon from '@mui/icons-material/Circle';
import { colors } from '../../../../theme/mui/colors';
import NotePencilIcon from '../../icons/NotePencilIcon';
import { MODAL_ID, MODALS_STORE } from '../../../../constants/ui/modal';
import { useEffect, useRef, useState } from 'react';
import { resendInvitationMutation } from '../../../../api/users/query';
import notifyHandler from '../../../../utils/notifyHandler';
import { GridRenderCellParams } from '@mui/x-data-grid';

const drawUserBadge = ({ label, color, borderRadius }: { label: string; color: string; borderRadius: number }) => {
  return (
    <Chip
      icon={<CircleIcon sx={{ fontSize: '0.75rem' }} />}
      label={label}
      variant="outlined"
      style={{ borderRadius: borderRadius, paddingLeft: '0.75rem', paddingRight: '0.75rem' }}
      sx={{
        '& .MuiChip-icon': {
          color,
        },
      }}
    />
  );
};

export const userAccessColumnsFn = (branchIdFn: () => string) => {
  const { t } = useLocalization();
  const { toggleModal } = MODALS_STORE.userUpdate();
  const theme = useTheme();
  const [idToSendingInvitationMap, setIdToSendingInvitationMap] = useState<Record<string, boolean>>({});
  const [invitationIdToSignalMap, setInvitationIdToSignalMap] = useState<Record<string, AbortController>>({});
  const invitationIdToSignalMapRef = useRef<Record<string, AbortController>>(invitationIdToSignalMap);
  const { mutateAsync: resendInvitationAsync } = resendInvitationMutation();

  useEffect(() => {
    invitationIdToSignalMapRef.current = invitationIdToSignalMap;
  }, [invitationIdToSignalMap]);

  const cancelInvitationRequestFn = () => {
    for (const key of Object.keys(invitationIdToSignalMapRef.current)) {
      invitationIdToSignalMapRef.current[key].abort();
    }

    setInvitationIdToSignalMap({});
  };

  const columnTemplates = [
    {
      headerName: t('common.terms.email'),
      field: 'email',
      minWidth: 350,
      sortable: false,
      render: (value: IUserBranchDto['email']) => {
        return <TextTooltip value={value} maxLength={35} />;
      },
    },
    {
      headerName: t('common.terms.fullName'),
      field: 'firstName',
      minWidth: 300,
      sortable: false,
      renderCell: (value: GridRenderCellParams<IUserBranchWithStatusDto, IUserBranchWithStatusDto['firstName']>) => {
        const record = value.row;
        if (record.status === UserStatuses.PENDING) {
          return '-';
        }
        return [record.firstName, record.lastName].join(' ');
      },
    },
    {
      headerName: t('common.terms.phoneNumber'),
      field: 'phone',
      minWidth: 300,
      sortable: false,
      renderCell: (value: GridRenderCellParams<IUserBranchWithStatusDto, IUserBranchWithStatusDto['phone']>) => {
        const record = value.row;
        if (record.status === UserStatuses.PENDING) {
          return '-';
        }
        return value.value;
      },
    },
    {
      headerName: t('common.terms.status'),
      field: 'status',
      width: 250,
      sortable: false,
      renderCell: ({
        value,
        row: record,
      }: GridRenderCellParams<IUserBranchWithStatusDto, IUserBranchWithStatusDto['status']>) => {
        if (value === UserStatuses.PENDING) {
          return drawUserBadge({
            label: t('common.userStatuses.pending'),
            color: colors.mono.level4_400,
            borderRadius: theme.shape.borderRadius,
          });
        }

        if (record.isAdmin) {
          return drawUserBadge({
            label: t('common.terms.admin'),
            color: colors.topaz.level50,
            borderRadius: theme.shape.borderRadius,
          });
        }

        return drawUserBadge({
          label: t('common.terms.user'),
          color: colors.emerald.level50,
          borderRadius: theme.shape.borderRadius,
        });
      },
    },
    {
      headerName: '',
      field: 'id',
      key: 'actions',
      width: 300,
      sortable: false,
      renderCell: ({ row: record }: GridRenderCellParams<IUserBranchWithStatusDto, IUserBranchWithStatusDto['id']>) => {
        if (record.status === UserStatuses.PENDING) {
          return (
            <Button
              variant="outlined"
              loading={idToSendingInvitationMap[record.id]}
              type="button"
              onClick={() => {
                setIdToSendingInvitationMap(value => {
                  value[record.id] = true;
                  return value;
                });

                if (invitationIdToSignalMap[record.id]) {
                  invitationIdToSignalMap[record.id].abort();
                }

                const abortController = new AbortController();
                setInvitationIdToSignalMap(value => ({
                  ...value,
                  [record.id]: abortController,
                }));

                resendInvitationAsync(
                  {
                    invitationId: record.id,
                    signal: abortController.signal,
                  },
                  {
                    onSuccess: () => {
                      notifyHandler({
                        type: 'success',
                        content: t('common.messages.invitationHasBeenResentToEmailX', { email: record.email }),
                      });
                    },
                    onError: () => {
                      notifyHandler({
                        type: 'error',
                        content: t('common.messages.somethingHappenedWhileReSendingInvitationToEmailX', {
                          email: record.email,
                        }),
                      });
                    },
                    onSettled: () => {
                      setIdToSendingInvitationMap(value => {
                        value[record.id] = false;
                        return value;
                      });
                    },
                  }
                );
              }}
            >
              {t('userAdministration.resendInvite')}
            </Button>
          );
        }
        return (
          <Button
            endIcon={<NotePencilIcon />}
            type="button"
            variant="outlined"
            onClick={() => {
              toggleModal(MODAL_ID.USER_UPDATE, {
                user: record as IUserBranchDto,
                branchId: branchIdFn(),
              });
            }}
          >
            {t('common.actions.editUser')}
          </Button>
        );
      },
    },
  ];

  return {
    columns: columnTemplates,
    cancelInvitationRequestFn,
  };
};
